import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import config from "../../config";
import "./profileregistration.css";

/* --- Custom SearchableDropdown Component --- */
const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  labelKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const getLabel = (option) => (labelKey ? option[labelKey] : option);

  const filteredOptions = options.filter((option) =>
    getLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="searchable-dropdown">
      <div
        className="dropdown-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {value ? getLabel(value) : placeholder}
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dropdown-search-input"
          />
          <ul className="dropdown-options">
            {filteredOptions.map((option, index) => (
              <li
                key={index}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
              >
                {getLabel(option)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* --- ProfileRegistration Component --- */
const ProfileRegistration = () => {
  const navigate = useNavigate();
  const { token, deviceId, userId, eventId, applicantId } = useSelector(
    (state) => state.auth
  );

  const [formData, setFormData] = useState({
    profilePic: null,
    username: "",
    fathername: "",
    gender: "",
    participantType: "",
    participantCategory: "",
    participationAs: "",
    pmdcredno: "",
    desjob: "",
    institution: "",
    school: "",
    country: "",
    zipcode: "",
    landlinenumber: "",
    phoneno: "",
    fax: "",
    email: "",
    altemail: "",
    city: "",
    department: "",
    address: "",
  });

  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  // For the searchable dropdown, store selected country's code.
  const [selectedCountryCode, setSelectedCountryCode] = useState("");

  // Search states for country and city dropdowns.
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");

  // Modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [firstErrorField, setFirstErrorField] = useState(null);

  useEffect(() => {
    if (userId) {
      setFormData((prev) => ({ ...prev, email: userId }));
    }
  }, [userId]);

  useEffect(() => {
    fetch("https://secure.geonames.org/countryInfoJSON?username=dawoodhaider")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.geonames) {
          const countryList = data.geonames.map((c) => ({
            country: c.countryName,
            code: c.countryCode,
          }));
          setCountries(countryList);
        }
      })
      .catch((err) => console.error("Error fetching countries:", err));
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    // Check file size (max 2MB)
    if (file && file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        profilePic: "File size must be less than 2MB.",
      }));
      return;
    }
    // Check file type: disallow webp
    if (file && file.type === "image/webp") {
      setErrors((prev) => ({
        ...prev,
        profilePic: "WebP format is not supported.",
      }));
      return;
    }
    handleChange("profilePic", file);
  };

  // Remove dangerous characters from input (except emails)
  const sanitizeInput = (value) => {
    return value.replace(/[&=?#%<>]/g, "");
  };

  const handleSanitizedChange = (field, value, maxLen) => {
    const truncated = value.substring(0, maxLen);
    const sanitized = sanitizeInput(truncated);
    handleChange(field, sanitized);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.profilePic) newErrors.profilePic = "This field is required.";
    if (!formData.username.trim()) newErrors.username = "This field is required.";
    if (!formData.fathername.trim())
      newErrors.fathername = "This field is required.";
    if (!formData.gender) newErrors.gender = "This field is required.";
    if (!formData.participantType)
      newErrors.participantType = "This field is required.";
    if (!formData.participantCategory)
      newErrors.participantCategory = "This field is required.";
    if (!formData.participationAs)
      newErrors.participationAs = "This field is required.";
    if (!formData.country) newErrors.country = "This field is required.";
    if (!formData.zipcode.trim()) newErrors.zipcode = "This field is required.";
    if (!formData.phoneno.trim()) newErrors.phoneno = "This field is required.";
    if (!formData.email.trim()) newErrors.email = "This field is required.";
    if (!formData.city.trim()) newErrors.city = "This field is required.";
    if (!formData.address.trim())
      newErrors.address = "This field is required.";
    setErrors(newErrors);
    return newErrors;
  };

  const convertFileToPng = (file, applicantId) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              const pngFile = new File([blob], `${applicantId}.png`, {
                type: "image/png",
              });
              resolve(pngFile);
            },
            "image/png"
          );
        };
        img.onerror = (err) => reject(err);
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  // Called when a country is selected from the dropdown.
  const handleCountrySelect = (option) => {
    setSelectedCountryCode(option.code);
    handleChange("country", option.country);
    // Fetch cities for the selected country.
    fetch(
      `https://secure.geonames.org/searchJSON?country=${option.code}&featureClass=P&maxRows=1000&username=dawoodhaider`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data && data.geonames) {
          const cityList = data.geonames.map((city) => city.name);
          const uniqueCities = Array.from(new Set(cityList)).sort();
          setCities(uniqueCities);
        } else {
          setCities([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching cities:", err);
        setCities([]);
      });
    handleChange("city", "");
  };

  // When user clicks OK in the error modal, close modal and scroll to first error.
  const handleModalOk = () => {
    setShowErrorModal(false);
    if (firstErrorField) {
      const el = document.getElementById(firstErrorField);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    const currentErrors = validateForm();
    if (Object.keys(currentErrors).length > 0) {
      setFirstErrorField(Object.keys(currentErrors)[0]);
      setShowErrorModal(true);
      return;
    }

    const nationality =
      formData.participantType === "Pakistan" ? "Pakistani" : "Foreigner";

    const url = config.API_PROFILE_CREATE_URL
      .replace("{DeviceID}", deviceId)
      .replace("{UserID}", encodeURIComponent(userId))
      .replace("{EventID}", eventId)
      .replace("{ApplicantID}", applicantId)
      .replace("{Name}", encodeURIComponent(formData.username))
      .replace("{FatherName}", encodeURIComponent(formData.fathername))
      .replace("{Nationality}", encodeURIComponent(nationality))
      .replace("{PartcipantType}", encodeURIComponent(formData.participantType))
      .replace("{PresenterType}", encodeURIComponent(formData.participationAs))
      .replace("{PMDCReg}", encodeURIComponent(formData.pmdcredno))
      .replace("{Designation}", encodeURIComponent(formData.desjob))
      .replace("{Institution}", encodeURIComponent(formData.institution))
      .replace("{Faculty}", encodeURIComponent(formData.school))
      .replace("{Department}", encodeURIComponent(formData.department))
      .replace("{Address}", encodeURIComponent(formData.address))
      .replace("{Country}", encodeURIComponent(formData.country))
      .replace("{PostCode}", encodeURIComponent(formData.zipcode))
      .replace("{City}", encodeURIComponent(formData.city))
      .replace("{Phone}", encodeURIComponent(formData.landlinenumber))
      .replace("{Mobile}", encodeURIComponent(formData.phoneno))
      .replace("{Fax}", encodeURIComponent(formData.fax))
      .replace("{AltEmail}", encodeURIComponent(formData.altemail))
      .replace("{Gender}", encodeURIComponent(formData.gender));

    try {
      const pngFile = await convertFileToPng(formData.profilePic, applicantId);
      const formDataToSend = new FormData();
      formDataToSend.append("ProfileImage", pngFile);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });
      const data = await response.json();
      const result = data.Table[0];
      if (result.Status === "Success") {
        alert("Profile created");
        navigate("/profile");
      } else {
        setErrors({
          general: "The profile cannot be created, please try again later.",
        });
      }
    } catch (error) {
      console.error("Profile creation error:", error);
      setErrors({
        general: "The profile cannot be created, please try again later.",
      });
    }
  };

  return (
    <>
      <form className="registation-form" onSubmit={handleVerification}>
        <table className="registration-table">
          <tbody>
            {/* Header Row: Title and Profile Picture Upload */}
            <tr>
              <td colSpan="2" className="form-header">
                <h2>Profile Registration</h2>
              </td>
              <td className="profile-pic-cell">
                <label htmlFor="profilePic" className="profile-pic-label">
                  Upload Profile Picture <span className="required">*</span>
                </label>
                <input
                  type="file"
                  id="profilePic"
                  name="profilePic"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                {errors.profilePic && (
                  <div className="error-message">{errors.profilePic}</div>
                )}
              </td>
            </tr>

            {/* Row for Name */}
            <tr>
              <td>
                <label htmlFor="username">
                  Name (to be Mentioned on the Certificate){" "}
                  <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Your Full Name"
                  maxLength="50"
                  value={formData.username}
                  onChange={(e) =>
                    handleSanitizedChange("username", e.target.value, 50)
                  }
                />
                {errors.username && (
                  <div className="error-message">{errors.username}</div>
                )}
              </td>
            </tr>

            {/* Row for Father's Name */}
            <tr>
              <td>
                <label htmlFor="fathername">
                  Father's Name <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="fathername"
                  name="fathername"
                  placeholder="Father's Name"
                  maxLength="50"
                  value={formData.fathername}
                  onChange={(e) =>
                    handleSanitizedChange("fathername", e.target.value, 50)
                  }
                />
                {errors.fathername && (
                  <div className="error-message">{errors.fathername}</div>
                )}
              </td>
            </tr>

            {/* Row for Gender */}
            <tr>
              <td>
                <label htmlFor="gender">
                  Gender <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <select
                  name="gender"
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => handleChange("gender", e.target.value)}
                >
                  <option value="" disabled hidden>
                    Select Gender
                  </option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender && (
                  <div className="error-message">{errors.gender}</div>
                )}
              </td>
            </tr>

            {/* Row for Participation Type */}
            <tr>
              <td>
                <label htmlFor="participanttype">
                  Participation Type <span className="required">*</span>
                </label>
              </td>
              <td>
                <select
                  name="participanttype"
                  id="partcitype"
                  value={formData.participantType}
                  onChange={(e) =>
                    handleChange("participantType", e.target.value)
                  }
                >
                  <option value="" disabled hidden>
                    Select Type
                  </option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="Foreigner">Foreigner</option>
                </select>
                {errors.participantType && (
                  <div className="error-message">{errors.participantType}</div>
                )}
              </td>
              <td>
                <select
                  name="participanttyp"
                  id="partcityp"
                  value={formData.participantCategory}
                  onChange={(e) =>
                    handleChange("participantCategory", e.target.value)
                  }
                >
                  <option value="" disabled hidden>
                    Select Category
                  </option>
                  <option value="Student">Student</option>
                  <option value="Nurse">Nurse</option>
                  <option value="Others">Others</option>
                </select>
                {errors.participantCategory && (
                  <div className="error-message">{errors.participantCategory}</div>
                )}
              </td>
            </tr>

            {/* Row for Participation As */}
            <tr>
              <td>
                <label htmlFor="participationAs">
                  Participation As <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <select
                  name="participationAs"
                  id="participationAs"
                  value={formData.participationAs}
                  onChange={(e) =>
                    handleChange("participationAs", e.target.value)
                  }
                >
                  <option value="" disabled hidden>
                    Select Option
                  </option>
                  <option value="Attendee">Attendee</option>
                  <option value="Presenter">Presenter</option>
                </select>
                {errors.participationAs && (
                  <div className="error-message">{errors.participationAs}</div>
                )}
              </td>
            </tr>

            {/* Row for PMDC Registration No */}
            <tr>
              <td>
                <label htmlFor="pmdcredno">PMDC Registration No</label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="pmdcredno"
                  name="pmdcredno"
                  placeholder="PMDC Registration No"
                  maxLength="50"
                  value={formData.pmdcredno}
                  onChange={(e) =>
                    handleSanitizedChange("pmdcredno", e.target.value, 50)
                  }
                />
              </td>
            </tr>

            {/* Row for Designation / Job Title */}
            <tr>
              <td>
                <label htmlFor="desjob">Designation / Job Title</label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="desjob"
                  name="desjob"
                  placeholder="Designation / Job Title"
                  maxLength="200"
                  value={formData.desjob}
                  onChange={(e) =>
                    handleSanitizedChange("desjob", e.target.value, 200)
                  }
                />
              </td>
            </tr>

            {/* Row for Institution */}
            <tr>
              <td>
                <label htmlFor="institution">Institution</label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="institution"
                  name="institution"
                  placeholder="Institution"
                  maxLength="200"
                  value={formData.institution}
                  onChange={(e) =>
                    handleSanitizedChange("institution", e.target.value, 200)
                  }
                />
              </td>
            </tr>

            {/* Row for Faculty / School / Center */}
            <tr>
              <td>
                <label htmlFor="school">Faculty / School / Center</label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="school"
                  name="school"
                  placeholder="Faculty / School / Center"
                  maxLength="50"
                  value={formData.school}
                  onChange={(e) =>
                    handleSanitizedChange("school", e.target.value, 50)
                  }
                />
              </td>
            </tr>

            {/* Row for Department */}
            <tr>
              <td>
                <label htmlFor="department">Department</label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="department"
                  name="department"
                  placeholder="Department"
                  maxLength="200"
                  value={formData.department}
                  onChange={(e) =>
                    handleSanitizedChange("department", e.target.value, 200)
                  }
                />
              </td>
            </tr>

            {/* Row for Address */}
            <tr>
              <td>
                <label htmlFor="address">
                  Address <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="address"
                  name="address"
                  placeholder="Address"
                  maxLength="500"
                  value={formData.address}
                  onChange={(e) =>
                    handleSanitizedChange("address", e.target.value, 500)
                  }
                />
                {errors.address && (
                  <div className="error-message">{errors.address}</div>
                )}
              </td>
            </tr>

            {/* Row for Country with Searchable Dropdown */}
            <tr>
              <td>
                <label htmlFor="country">
                  Country <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <SearchableDropdown
                  options={countries}
                  value={countries.find((c) => c.code === selectedCountryCode)}
                  onChange={handleCountrySelect}
                  placeholder="Select Country"
                  labelKey="country"
                />
                {errors.country && (
                  <div className="error-message">{errors.country}</div>
                )}
              </td>
            </tr>

            {/* Row for City with Searchable Dropdown */}
            <tr>
              <td>
                <label htmlFor="city">
                  City <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <SearchableDropdown
                  options={cities}
                  value={formData.city}
                  onChange={(selectedCity) => handleChange("city", selectedCity)}
                  placeholder="Select City"
                />
                {errors.city && (
                  <div className="error-message">{errors.city}</div>
                )}
              </td>
            </tr>

            {/* Row for Zip / Postal Code */}
            <tr>
              <td>
                <label htmlFor="zipcode">
                  Zip / Postal Code <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="zipcode"
                  name="zipcode"
                  placeholder="Zip / Postal Code"
                  maxLength="10"
                  value={formData.zipcode}
                  onChange={(e) =>
                    handleSanitizedChange("zipcode", e.target.value, 10)
                  }
                />
                {errors.zipcode && (
                  <div className="error-message">{errors.zipcode}</div>
                )}
              </td>
            </tr>

            {/* Row for Landline Number */}
            <tr>
              <td>
                <label htmlFor="landlinenumber">Landline Number</label>
              </td>
              <td colSpan="2">
                <input
                  type="number"
                  id="landlinenumber"
                  name="landlinenumber"
                  placeholder="Landline Number"
                  maxLength="16"
                  value={formData.landlinenumber}
                  onChange={(e) =>
                    handleChange("landlinenumber", e.target.value)
                  }
                />
                <small className="input-hint">
                  only numbers without space or special characters
                </small>
              </td>
            </tr>

            {/* Row for Mobile */}
            <tr>
              <td>
                <label htmlFor="phoneno">
                  Mobile <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <input
                  type="tel"
                  id="phoneno"
                  name="phoneno"
                  placeholder="Mobile Number"
                  maxLength="15"
                  value={formData.phoneno}
                  onChange={(e) => handleChange("phoneno", e.target.value)}
                />
                <small className="input-hint">
                  only numbers without space or special characters
                </small>
                {errors.phoneno && (
                  <div className="error-message">{errors.phoneno}</div>
                )}
              </td>
            </tr>

            {/* Row for Fax */}
            <tr>
              <td>
                <label htmlFor="fax">Fax</label>
              </td>
              <td colSpan="2">
                <input
                  type="text"
                  id="fax"
                  name="fax"
                  placeholder="Fax"
                  maxLength="30"
                  value={formData.fax}
                  onChange={(e) =>
                    handleSanitizedChange("fax", e.target.value, 30)
                  }
                />
              </td>
            </tr>

            {/* Row for Email (Read Only) */}
            <tr>
              <td>
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
              </td>
              <td colSpan="2">
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="hello@example.com"
                  maxLength="100"
                  value={formData.email}
                  readOnly
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </td>
            </tr>

            {/* Row for Alternate Email */}
            <tr>
              <td>
                <label htmlFor="altemail">Alternate Email</label>
              </td>
              <td colSpan="2">
                <input
                  type="email"
                  id="altemail"
                  name="altemail"
                  placeholder="hello@example.com"
                  maxLength="100"
                  value={formData.altemail}
                  onChange={(e) => handleChange("altemail", e.target.value)}
                />
              </td>
            </tr>

            {/* Final Row: Submit Button */}
            <tr>
              <td colSpan="3" style={{ textAlign: "center" }}>
                <button type="submit" className="next-button">
                  Next
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {/* Error Modal */}
      {showErrorModal && (
        <div className="error-modal-overlay">
          <div className="error-modal">
            <h3>Required Fields Missing</h3>
            <p>Please fill in the following required fields:</p>
            <ul>
              {Object.keys(errors).map((field, index) => (
                <li key={index}>
                  {field} - {errors[field]}
                </li>
              ))}
              <li>Image size should be less than 2MB.</li>
            </ul>
            <button onClick={handleModalOk}>OK</button>
          </div>
        </div>
      )}

      {/* Floating Alert Always Visible */}
      <div className="floating-alert">
        Image should be less than 2MB.
      </div>
    </>
  );
};

export default ProfileRegistration;
